import React, { useState, useMemo } from 'react';
import { useCard } from '../../themes/cardStyles';
import { buildPaletteTheme, hpColor } from '../../themes/palettes';
import { CharacterAvatar } from '../primitives/CharacterAvatar';
import { ResourceBar } from '../primitives/ResourceBar';
import { DemoButton } from '../primitives/buttons';

export const CharacterRow = ({ theme, density, ch, palette, cardStyle, cardFinish, onPlay, onRemove }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [hovered, setHovered] = useState(false);
  const canPlay = typeof onPlay === 'function';
  const canRemove = typeof onRemove === 'function';

  const tintedTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish),
    [theme, palette, cardStyle, cardFinish]
  );
  const cardObj = useCard(tintedTheme, cardStyle, hovered);

  return (
    <div
      role={canPlay ? 'button' : undefined}
      aria-label={canPlay ? `Open ${ch.name}` : undefined}
      tabIndex={canPlay ? 0 : undefined}
      onClick={canPlay ? onPlay : undefined}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          if (canPlay) onPlay();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardObj,
        border: cardObj.border || `1px solid ${palette.color}40`,
        padding: '14px 16px',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr minmax(180px, 220px) auto',
        gap: 16,
        alignItems: 'center',
        cursor: canPlay ? 'pointer' : 'default',
        transition: e.transition,
      }}
    >
      <div style={{
        width: 48, height: 48,
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.4)',
        border: `2px solid ${palette.color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        boxShadow: `0 0 16px ${palette.glow}`,
        flexShrink: 0,
      }}>
        <CharacterAvatar char={ch} size={48} color={palette.light} fontFamily={t.headingFamily} />
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: t.headingFamily,
            fontSize: '1.15rem',
            fontWeight: 600,
            color: palette.light,
            textShadow: `${e.headingTextShadow || ''}, 0 0 18px rgba(0,0,0,0.55)`,
          }}>{ch.name}</span>
          {ch.isTestCharacter && (
            <span style={{
              fontSize: '0.62rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: palette.light,
              border: `1px solid ${palette.color}99`,
              borderRadius: 999,
              padding: '1px 7px',
              fontFamily: t.fontFamily,
            }}>Practice</span>
          )}
        </div>
        <div style={{
          fontSize: '0.78rem', color: c.textMuted, marginTop: 2,
          fontFamily: t.fontFamily,
        }}>
          {ch.race} {ch.cls} - Level {ch.level}
          {ch.note && (
            <span style={{ color: c.warning, fontStyle: 'italic' }}> - {ch.note}</span>
          )}
        </div>
      </div>

      <div>
        <ResourceBar
          label="HP" current={ch.hp} max={ch.maxHp}
          color={hpColor(ch.hp, ch.maxHp, theme)}
          theme={theme} compact
        />
        {ch.mp != null && (
          <div style={{ marginTop: 6 }}>
            <ResourceBar
              label="MP" current={ch.mp} max={ch.maxMp}
              color={c.mana || c.info} theme={theme} compact
            />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {canPlay ? (
          <DemoButton
            theme={tintedTheme}
            icon=">"
            onClick={(ev) => {
              ev?.stopPropagation?.();
              onPlay();
            }}
          >
            Continue
          </DemoButton>
        ) : (
          <span style={{
            padding: '7px 12px',
            border: `1px solid ${c.borderLight}`,
            borderRadius: e.borderRadiusSm || '6px',
            color: c.textMuted,
            fontFamily: t.fontFamily,
            fontSize: '0.82rem',
          }}>Roster</span>
        )}
        {canRemove && (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              onRemove();
            }}
            onKeyDown={(ev) => ev.stopPropagation()}
            style={{
              padding: '7px 12px',
              border: `1px solid ${c.danger || '#d85c5c'}88`,
              borderRadius: e.borderRadiusSm || '6px',
              background: 'rgba(0,0,0,0.25)',
              color: c.danger || '#ff8f8f',
              cursor: 'pointer',
              fontFamily: t.fontFamily,
              fontSize: '0.82rem',
            }}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};
